import CardErrorMessage from 'crm-cards-sdk/components/CardErrorMessage';
import { useI18nLoader } from 'crm-cards-sdk/hooks/useI18nLoader';
import { useCardLocationContext } from 'crm-cards-sdk/internal/CardLocationContext';
import { useCardErrorMarker } from 'crm-cards-sdk/internal/perf/cardPerfHooks';
import FormattedCard from 'crm-cards-sdk/components/FormattedCard';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { memoizeRegisterTranslations } from 'crm-cards-sdk/internal/memoizeRegisterTranslations';
import { Metrics } from './Metrics';
import devLogger from 'react-utils/devLogger';
import CardLoadingState from 'crm-cards-sdk/components/CardLoadingState';
import measure from 'crm-fe-perf/timing/measure';
import { getDownloadStart, getDownloadFinish } from 'crm-fe-perf/timing/marks/crm-cards-sdk/performanceMarks';
import { getDownloadMeasure, getCardRenderStartToRealContentStart } from 'crm-fe-perf/timing/measurements/crm-cards-sdk/performanceMeasurements';
import mark from 'crm-fe-perf/timing/mark';
import { isLegacyCard } from 'crm-cards-sdk/utils/isLegacyCard';
import { getWasHidden } from 'crm-cards-sdk/utils/sharedPerfDimensions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TRANSLATION_TIMEOUT_PERIOD_MS = 60000;
function isErrorState(loadingState) {
  switch (loadingState) {
    case 'TIMEOUT':
    case 'ERROR':
      return true;
    default:
      return false;
  }
}
const initialState = {};
function reducer(state, action) {
  switch (action.type) {
    case 'LOADING_TIMED_OUT':
      return Object.assign({}, state, {
        [action.cardType]: 'TIMEOUT'
      });
    case 'LOADING_STARTED':
      return Object.assign({}, state, {
        [action.cardType]: 'LOADING'
      });
    case 'LOADING_SUCCEEDED':
      {
        if (state[action.cardType] === 'TIMEOUT') {
          Metrics.counter('card-translation-error', Object.assign({
            card_type: action.cardType,
            location: action.location,
            error_type: 'TIMEOUT_RECOVERED'
          }, action.loggingTag ? {
            logging_tag: action.loggingTag
          } : {})).increment();
        }
        return Object.assign({}, state, {
          [action.cardType]: 'SUCCESS'
        });
      }
    case 'LOADING_FAILED':
      return Object.assign({}, state, {
        [action.cardType]: 'ERROR'
      });
    default:
      throw new Error();
  }
}
const LOCAL_STORAGE_KEY = 'CRM_SDK:SHOW_CARD_LOADING_STATE';
function useShowLoadingStateForDebug() {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === 'true';
  } catch (e) {
    return false;
  }
}
const startMarkFiredMap = new Map();
const finishMarkFiredMap = new Map();

/**
 * @param dangerouslyDisableMemoization - This is only used for testing in order to disable memoization
 */
export default function CardTranslationsLoader({
  children,
  registry,
  cardData,
  indicatorDelay = 200,
  CardFormatter,
  dangerouslyDisableMemoization = false
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [pastDelay, setPastDelay] = useState(false);
  const timeoutIdRef = useRef(undefined);
  const cardType = cardData.definition.cardType;
  const lazyLoadedTranslations = registry.getLazyLoadedTranslations(cardType);
  const currentTranslationState = state[cardType];
  const i18nLoader = useI18nLoader();
  const crmLocationContext = useCardLocationContext();
  const {
    location,
    loggingTag
  } = crmLocationContext;
  const ErrorMarker = useCardErrorMarker({
    type: 'TRANSLATION'
  });
  const showLoadingStateForDebug = useShowLoadingStateForDebug();
  // These values shouldn't change in a rerender, but use ref to ensure we won't re-try loading translations
  const dimensionsRef = useRef({
    location,
    logging_tag: loggingTag || '',
    card_type: cardType
  });
  const hasMeasuredRef = useRef(false);
  const loadTranslations = useCallback(() => {
    if (lazyLoadedTranslations === undefined) {
      return;
    }
    dispatch({
      cardType,
      type: 'LOADING_STARTED',
      location,
      loggingTag
    });
    if (!startMarkFiredMap.get(cardType)) {
      // Only mark the FIRST time we try to download
      startMarkFiredMap.set(cardType, true);
      mark(getDownloadStart(cardType, 'translations'), dimensionsRef.current, true);
    }

    // track how many times we attempt loading a card's translations so we have a baseline
    Metrics.counter('card-translations-load-started', Object.assign({
      card_type: cardType,
      location,
      is_retry: 'false'
    }, loggingTag ? {
      logging_tag: loggingTag
    } : {})).increment();
    const startTime = Date.now();
    // start a timeout in case the translations never load so we eventually show an error
    timeoutIdRef.current = setTimeout(() => {
      dispatch({
        cardType,
        type: 'LOADING_TIMED_OUT',
        location,
        loggingTag
      });
    }, TRANSLATION_TIMEOUT_PERIOD_MS);
    /**
     * This memoizes requests to register translations so that all cards of the same cardType get returned
     * the same exact promise. Otherwise, the cards will receive different promises, and some will resolve
     * before translations are actually loaded. Only the first argument is used to calculate the memoization key.
     */
    memoizeRegisterTranslations({
      lazyLoadedTranslations,
      i18nLoader,
      // This is only used for testing
      dangerouslyDisableMemoization
    }).then(() => {
      clearTimeout(timeoutIdRef.current);
      dispatch({
        cardType,
        type: 'LOADING_SUCCEEDED',
        location,
        loggingTag
      });
      if (!finishMarkFiredMap.get(cardType)) {
        mark(getDownloadFinish(cardType, 'translations'), dimensionsRef.current, true);
        measure(getDownloadMeasure(cardType, 'translations'), Object.assign({}, dimensionsRef.current, {
          was_hidden: getWasHidden(),
          start: '',
          // hardcode this to reduce cardinality
          end: '' // hardcode this to reduce cardinality
        }));
        finishMarkFiredMap.set(cardType, true);
      }
      Metrics.timer('card-translation-load-finished', Object.assign({
        loading_state: 'SUCCESS',
        card_type: cardType,
        location
      }, loggingTag ? {
        logging_tag: loggingTag
      } : {})).update(Date.now() - startTime);
    }).catch(() => {
      clearTimeout(timeoutIdRef.current);
      dispatch({
        cardType,
        type: 'LOADING_FAILED',
        location,
        loggingTag
      });
      Metrics.timer('card-translation-load-finished', Object.assign({
        loading_state: 'ERROR',
        card_type: cardType,
        location,
        is_retry: 'false'
      }, loggingTag ? {
        logging_tag: loggingTag
      } : {})).update(Date.now() - startTime);
    });
  }, [timeoutIdRef, cardType, i18nLoader, lazyLoadedTranslations, location, loggingTag, dangerouslyDisableMemoization]);
  useEffect(() => {
    loadTranslations();
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [loadTranslations]);
  useEffect(() => {
    if (isErrorState(currentTranslationState)) {
      Metrics.counter('card-translation-error', Object.assign({
        card_type: cardData.definition.cardType,
        location,
        error_type: currentTranslationState
      }, loggingTag ? {
        logging_tag: loggingTag
      } : {})).increment();
    }
  }, [currentTranslationState, cardData, location, loggingTag]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPastDelay(true);
    }, indicatorDelay);
    return () => clearTimeout(timeoutId);
  }, [indicatorDelay]);
  if (currentTranslationState === 'TIMEOUT') {
    devLogger.warn({
      key: `CardTranslationsLoader:Timeout:${cardType}`,
      message: 'CRM SDK timed out trying to dynamically load card translations. Make sure there is only one I18nProvider instance in your app.'
    });
  }
  if (currentTranslationState === 'ERROR' || currentTranslationState === 'TIMEOUT') {
    return /*#__PURE__*/_jsxs(FormattedCard, {
      cardData: cardData,
      CardFormatter: CardFormatter,
      children: [/*#__PURE__*/_jsx(ErrorMarker, {}), /*#__PURE__*/_jsx(CardErrorMessage, {})]
    });
  }
  if (lazyLoadedTranslations && (!currentTranslationState || currentTranslationState === 'LOADING') || showLoadingStateForDebug) {
    return /*#__PURE__*/_jsx(CardLoadingState, {
      pastDelay: pastDelay,
      CardFormatter: CardFormatter
    });
  }
  if (!hasMeasuredRef.current) {
    hasMeasuredRef.current = true;
    const exactCardType = isLegacyCard(cardData) ? cardData.configuration.cardType : cardData.definition.cardType;
    measure(getCardRenderStartToRealContentStart(exactCardType, cardData.definition.cardId), Object.assign({}, dimensionsRef.current, {
      card_type: exactCardType,
      was_hidden: getWasHidden()
    }), true);
  }
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
}